import React, { useState } from 'react';
import BodyPainVisualizer from '../../components/bodydiagrams/BodyPainVisualizer';
import { FaClipboardList } from 'react-icons/fa';

const bodyAreas = [
	"Head/Neck", "Shoulder", "Upper Back", "Lower Back", "Chest",
	"Abdomen", "Hip", "Knee", "Ankle/Foot", "Upper Arm", "Elbow", "Wrist/Hand"
];

interface ProcedureData {
	id?: number;
	year: string;
	procedure: string;
	performedBy: string;
	procedureType: string;
	bodyArea: string;
	completed: boolean;
}

const EMPTY_PROCEDURE_DATA: ProcedureData = {
	year: "",
	procedure: "",
	performedBy: "",
	procedureType: "",
	bodyArea: "",
	completed: false
};

interface ProcedureModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (data: ProcedureData) => void;
	editData?: ProcedureData | null;
}

const ProcedureModal = ({ isOpen, onClose, onSave, editData = null }: ProcedureModalProps) => {
	const [procedureData, setProcedureData] = useState<ProcedureData>(
		editData || EMPTY_PROCEDURE_DATA
	);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault(); // Prevent form submission
		e.stopPropagation(); // Stop event bubbling

		const submissionData: ProcedureData = {
			...procedureData,
			completed: procedureData.completed || false,
			id: editData ? editData.id : Date.now()
		};

		onSave(submissionData);
		setProcedureData(EMPTY_PROCEDURE_DATA);
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
			<div className="bg-base-100 rounded-lg p-6 w-full max-w-2xl border border-base-300 shadow-lg">
				<div className="flex justify-between items-center mb-4">
					<h2 className="text-xl font-semibold text-base-content">
						{editData ? 'Edit Procedure' : 'Add New Procedure'}
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="btn btn-sm btn-ghost btn-circle"
					>
						✕
					</button>
				</div>

				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="grid grid-cols-2 gap-4">
						<div>
							<label className="label">
								<span className="label-text">Year</span>
							</label>
							<select
								className="select select-bordered w-full"
								value={procedureData.year}
								onChange={(e) => setProcedureData({
									...procedureData,
									year: e.target.value
								})}
								required
							>
								<option value="">Select Year</option>
								{Array.from(
									{ length: 50 },
									(_, i) => new Date().getFullYear() - i
								).map(year => (
									<option key={year} value={year}>
										{year}
									</option>
								))}
							</select>
						</div>

						<div>
							<label className="label">
								<span className="label-text">Body Area</span>
							</label>
							<select
								className="select select-bordered w-full"
								value={procedureData.bodyArea}
								onChange={(e) => setProcedureData({
									...procedureData,
									bodyArea: e.target.value
								})}
								required
							>
								<option value="">Select Area</option>
								{bodyAreas.map(area => (
									<option key={area} value={area}>
										{area}
									</option>
								))}
							</select>
						</div>

						<div className="col-span-2">
							<label className="label">
								<span className="label-text">Procedure</span>
							</label>
							<input
								type="text"
								className="input input-bordered w-full"
								value={procedureData.procedure}
								onChange={(e) => setProcedureData({
									...procedureData,
									procedure: e.target.value
								})}
								required
							/>
						</div>

						<div>
							<label className="label">
								<span className="label-text">Performed by</span>
							</label>
							<input
								type="text"
								className="input input-bordered w-full"
								value={procedureData.performedBy}
								onChange={(e) => setProcedureData({
									...procedureData,
									performedBy: e.target.value
								})}
								required
							/>
						</div>

						<div>
							<label className="label">
								<span className="label-text">Procedure Type</span>
							</label>
							<input
								type="text"
								className="input input-bordered w-full"
								value={procedureData.procedureType}
								onChange={(e) => setProcedureData({
									...procedureData,
									procedureType: e.target.value
								})}
								required
							/>
						</div>
					</div>

					<div className="flex justify-end space-x-3 pt-4">
						<button
							type="button"
							onClick={onClose}
							className="btn btn-ghost"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={handleSubmit}
							className="btn btn-primary"
						>
							{editData ? 'Update' : 'Add'} Procedure
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

interface PainWorkupFormData {
	date: string;
	workNumber: string;
	insuranceNumber: string;
	subjective: string;
	working: string;
	social: string;
	painLocation: string;
	painSize: string;
	painDuration: string;
	painFirstCommenced: string;
	painFrequency: string;
	averagePain: string;
	painCharacteristics: string[];
	severityAt: string;
	severityWorst: string;
	cycleMorning: string;
	cycleEvening: string;
	exacerbatingActivities: string;
	painReductionStrategies: string;
	diagnoses: string[];
}

const PainWorkupSheet = () => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingProcedure, setEditingProcedure] = useState<ProcedureData | null>(null);

	const patientData = {
		name: "Sarah Johnson",
		dob: "31/05/1988"
	};

	const painFrequencyOptions = [
		"Constant", "Occasional", "When moving only",
		"Intermittent", "Daily", "Weekly"
	];

	const painCharacteristics = [
		"Ache", "Throbbing", "Stabbing", "Clicks", "Pins & Needles",
		"Tender", "Burning", "Tingling", "Numbness", "Sharp", "Spasm", "Tremors"
	];

	const [procedures, setProcedures] = useState<ProcedureData[]>([]);
	const currentYear = new Date().getFullYear();
	const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

	const [formData, setFormData] = useState<PainWorkupFormData>({
		date: "",
		workNumber: "",
		insuranceNumber: "",
		subjective: "",
		working: "",
		social: "",
		painLocation: "",
		painSize: "",
		painDuration: "",
		painFirstCommenced: "",
		painFrequency: "",
		averagePain: "0",
		painCharacteristics: [],
		severityAt: "0",
		severityWorst: "0",
		cycleMorning: "",
		cycleEvening: "",
		exacerbatingActivities: "",
		painReductionStrategies: "",
		diagnoses: []
	});

	const handleSaveProcedure = (procedureData: ProcedureData) => {
		const newProcedureData: ProcedureData = {
			...procedureData,
			completed: procedureData.completed || false,
			id: editingProcedure ? editingProcedure.id : Date.now()
		};

		if (editingProcedure) {
			const updatedProcedures = procedures.map(proc =>
				proc.id === editingProcedure.id ? newProcedureData : proc
			);
			setProcedures(updatedProcedures);
		} else {
			setProcedures([...procedures, newProcedureData]);
		}

		setIsModalOpen(false);
		setEditingProcedure(null);
	};

	const handleEdit = (procedure: ProcedureData) => {
		setEditingProcedure(procedure);
		setIsModalOpen(true);
	};

	const removeProcedure = (id?: number) => {
		setProcedures(procedures.filter(proc => proc.id !== id));
	};

	const handleSave = async () => {
		try {
			setIsSubmitting(true);
			console.log('Saving pain workup data:', formData);
		} catch (error) {
			console.error('Error saving data:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSubmit = async () => {
		try {
			setIsSubmitting(true);
			console.log('Submitting pain workup data:', formData);
		} catch (error) {
			console.error('Error submitting data:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="p-6">
			{isSubmitting && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-base-100 p-4 rounded-lg shadow-lg border border-base-300">
						<p className="text-base-content">Processing...</p>
					</div>
				</div>
			)}

			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
					<FaClipboardList className="text-primary" />
					Pain Workup Sheet • {patientData.name}
				</h1>
			</div>

			<form className="space-y-6">
				<div className="grid grid-cols-2 gap-6">
					{/* Left Column */}
					<div className="space-y-6">
						{/* Patient Info */}
						<div className="grid grid-cols-2 gap-4 p-4 border border-base-300 rounded-lg shadow-sm bg-base-200">
							<div>
								<div className="mb-4">
									<label className="label">
										<span className="label-text">Patient Name</span>
									</label>
									<input
										type="text"
										className="input input-bordered w-full"
										value={patientData.name}
										disabled
									/>
								</div>
								<div>
									<label className="label">
										<span className="label-text">Date of Birth</span>
									</label>
									<input
										type="text"
										className="input input-bordered w-full"
										value={patientData.dob}
										disabled
									/>
								</div>
							</div>
							<div>
								<div className="mb-4">
									<label className="label">
										<span className="label-text">Date</span>
									</label>
									<input
										type="date"
										className="input input-bordered w-full"
										onChange={(e) => setFormData({ ...formData, date: e.target.value })}
									/>
								</div>
								<div>
									<label className="label">
										<span className="label-text">Insurance Number</span>
									</label>
									<input
										type="text"
										className="input input-bordered w-full"
										placeholder="Enter insurance number"
										onChange={(e) => setFormData({ ...formData, insuranceNumber: e.target.value })}
									/>
								</div>
							</div>
						</div>

						{/* Work & Social */}
						<div className="grid grid-cols-2 gap-4">
							<div>
								<label className="label">
									<span className="label-text">Working</span>
								</label>
								<input
									type="text"
									className="input input-bordered w-full"
									placeholder="Occupation"
									onChange={(e) => setFormData({ ...formData, working: e.target.value })}
								/>
							</div>
							<div>
								<label className="label">
									<span className="label-text">Social</span>
								</label>
								<input
									type="text"
									className="input input-bordered w-full"
									placeholder="Social details"
									onChange={(e) => setFormData({ ...formData, social: e.target.value })}
								/>
							</div>
						</div>

						{/* Procedures Table */}
						<div className="border border-base-300 rounded-lg p-4 bg-base-200">
							<div className="flex justify-between items-center mb-4">
								<h3 className="text-lg font-semibold text-base-content">Procedures</h3>
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										setEditingProcedure(null);
										setIsModalOpen(true);
									}}
									className="btn btn-primary"
								>
									Create
								</button>
							</div>
							<div className="overflow-x-auto">
								<table className="table table-zebra">
									<thead>
										<tr>
											<th></th>
											<th>Year</th>
											<th>Procedure</th>
											<th>Performed by</th>
											<th>Procedure Type</th>
											<th>Body Area</th>
											<th>Commands</th>
										</tr>
									</thead>
									<tbody>
										{procedures.map((proc) => (
											<tr key={proc.id}>
												<td>
													<input
														type="checkbox"
														checked={proc.completed}
														onChange={() => {
															const updatedProcedures = procedures.map(p =>
																p.id === proc.id ? { ...p, completed: !p.completed } : p
															);
															setProcedures(updatedProcedures);
														}}
														className="checkbox checkbox-primary"
													/>
												</td>
												<td>{proc.year}</td>
												<td>{proc.procedure}</td>
												<td>{proc.performedBy}</td>
												<td>{proc.procedureType}</td>
												<td>{proc.bodyArea}</td>
												<td>
													<div className="flex gap-2">
														<button
															type="button"
															onClick={() => handleEdit(proc)}
															className="btn btn-ghost btn-xs"
														>
															Edit
														</button>
														<button
															type="button"
															onClick={() => removeProcedure(proc.id)}
															className="btn btn-ghost btn-xs text-error"
														>
															Remove
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>

						{/* Rest of the form sections */}
						{/* Pain Assessment */}
						<div className="border border-base-300 rounded-lg p-4 space-y-6 bg-base-200">
							<h3 className="text-lg font-semibold text-base-content">Pain Assessment</h3>

							<div className="grid grid-cols-3 gap-4">
								<div>
									<label className="label">
										<span className="label-text">Location</span>
									</label>
									<select
										className="select select-bordered w-full"
										onChange={(e) => setFormData({ ...formData, painLocation: e.target.value })}
									>
										<option value="">Select location</option>
										<option value="left_lb">Left LB</option>
										<option value="right_lb">Right LB</option>
										<option value="other">Other</option>
									</select>
								</div>
								<div>
									<label className="label">
										<span className="label-text">First Commenced</span>
									</label>
									<select
										className="select select-bordered w-full"
										onChange={(e) => setFormData({ ...formData, painFirstCommenced: e.target.value })}
									>
										<option value="">Select Year</option>
										{years.map(year => (
											<option key={year} value={year}>{year}</option>
										))}
									</select>
								</div>
								<div>
									<label className="label">
										<span className="label-text">Pain Frequency</span>
									</label>
									<select
										className="select select-bordered w-full"
										onChange={(e) => setFormData({ ...formData, painFrequency: e.target.value })}
									>
										<option value="">Select Frequency</option>
										{painFrequencyOptions.map(option => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
								</div>
							</div>

							<div>
								<label className="label">
									<span className="label-text">Pain Characteristics</span>
								</label>
								<div className="grid grid-cols-3 gap-4">
									{painCharacteristics.map((char) => (
										<label key={char} className="label cursor-pointer justify-start gap-2">
											<input type="checkbox"
												className="checkbox checkbox-primary"
												onChange={(e) => {
													const updatedChar = e.target.checked
														? [...formData.painCharacteristics, char]
														: formData.painCharacteristics.filter(c => c !== char);
													setFormData({ ...formData, painCharacteristics: updatedChar });
												}}
											/>
											<span className="label-text">{char}</span>
										</label>
									))}
								</div>
							</div>

							{/* Pain Severity */}
							<div className="grid grid-cols-3 gap-4">
								<div>
									<label className="label">
										<span className="label-text">Average Pain Score</span>
									</label>
									<input
										type="number"
										min="0"
										max="10"
										className="input input-bordered w-full"
										value={formData.averagePain}
										onChange={(e) => setFormData({ ...formData, averagePain: e.target.value })}
									/>
								</div>
								<div>
									<label className="label">
										<span className="label-text">Severity At Rest</span>
									</label>
									<input
										type="number"
										min="0"
										max="10"
										className="input input-bordered w-full"
										value={formData.severityAt}
										onChange={(e) => setFormData({ ...formData, severityAt: e.target.value })}
									/>
								</div>
								<div>
									<label className="label">
										<span className="label-text">Severity Worst</span>
									</label>
									<input
										type="number"
										min="0"
										max="10"
										className="input input-bordered w-full"
										value={formData.severityWorst}
										onChange={(e) => setFormData({ ...formData, severityWorst: e.target.value })}
									/>
								</div>
							</div>
						</div>

						{/* 24 Hour Cycle Card */}
						<div className="border border-base-300 rounded-lg shadow-sm bg-base-200 p-4 mb-6">
							<div className="space-y-4">
								<h3 className="text-lg font-semibold text-base-content">24 Hour Cycle</h3>
								<div className="grid grid-cols-2 gap-4">
									<div>
										<label className="label">
											<span className="label-text">a.m.</span>
										</label>
										<textarea
											className="textarea textarea-bordered w-full"
											rows={2}
											onChange={(e) => setFormData({ ...formData, cycleMorning: e.target.value })}
										/>
									</div>
									<div>
										<label className="label">
											<span className="label-text">p.m.</span>
										</label>
										<textarea
											className="textarea textarea-bordered w-full"
											rows={2}
											onChange={(e) => setFormData({ ...formData, cycleEvening: e.target.value })}
										/>
									</div>
								</div>
							</div>
						</div>

						{/* Aggravations and Ease */}
						<div className="border border-base-300 rounded-lg shadow-sm bg-base-200 p-4">
							<h3 className="text-lg font-semibold text-base-content">Aggravations</h3>
							<div>
								<label className="label">
									<span className="label-text">Exacerbating Activities</span>
								</label>
								<textarea
									className="textarea textarea-bordered w-full"
									rows={3}
									onChange={(e) => setFormData({ ...formData, exacerbatingActivities: e.target.value })}
								/>
							</div>
						</div>

						<div className="border border-base-300 rounded-lg shadow-sm bg-base-200 p-4">
							<h3 className="text-lg font-semibold text-base-content">Ease / Rest</h3>
							<div>
								<label className="label">
									<span className="label-text">Assistive Measures / Pain Reduction Strategies</span>
								</label>
								<textarea
									className="textarea textarea-bordered w-full"
									rows={3}
									onChange={(e) => setFormData({ ...formData, painReductionStrategies: e.target.value })}
								/>
							</div>
						</div>
					</div>

					{/* Right Column */}
					<div className="space-y-6">
						{/* Body Pain Visualizer Component */}
						<div className="border border-base-300 rounded-lg p-4 bg-base-200 shadow-sm">
							<h3 className="text-lg font-semibold text-base-content mb-4">Pain Location Diagram</h3>
							<BodyPainVisualizer />
						</div>

						{/* Diagnoses Section */}
						<div className="border border-base-300 rounded-lg p-4 bg-base-200">
							<h3 className="text-lg font-semibold text-base-content mb-3">Diagnoses</h3>
							<select
								multiple
								className="select select-bordered w-full h-32"
								onChange={(e) => {
									const selected = Array.from(e.target.selectedOptions, option => option.value);
									setFormData({ ...formData, diagnoses: selected });
								}}
							>
								<option value="anxiety">Anxiety</option>
								<option value="depression">Depression</option>
								<option value="bpd">Borderline Personality Disorder</option>
								<option value="diabetic">Diabetic</option>
								<option value="allergies">Allergies</option>
							</select>
						</div>
					</div>
				</div>

				{/* Action Buttons */}
				<div className="flex justify-end space-x-4">
					<button
						type="button"
						onClick={handleSave}
						disabled={isSubmitting}
						className="btn btn-primary"
					>
						Save Progress
					</button>
					<button
						type="button"
						onClick={handleSubmit}
						disabled={isSubmitting}
						className="btn btn-success"
					>
						Submit
					</button>
				</div>

				{/* Move ProcedureModal here */}
				<ProcedureModal
					isOpen={isModalOpen}
					onClose={() => {
						setIsModalOpen(false);
						setEditingProcedure(null);
					}}
					onSave={handleSaveProcedure}
					editData={editingProcedure}
				/>
			</form>
		</div>
	);
};

export default PainWorkupSheet;
